import React, { useCallback, useEffect, useId, useState } from "react";
import { useLocation } from "react-router-dom";
import { Menu } from "antd";
import { useTranslation } from "react-i18next";
import "../css/header.less";
import { getImageUrl } from "../config";
import LanguageSwitcher from "../components/LanguageSwitcher";
import LocalizedLink from "../components/LocalizedLink";
import { stripLangPrefix } from "../i18n/routing";

const HEADER_TEL_HREF = "tel:0442806828";
const HEADER_TEL_TEXT = "044-280-6828";

const AppHeader: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const selectedKey = stripLangPrefix(location.pathname);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const drawerTitleId = useId();

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const openDrawer = useCallback(() => setDrawerOpen(true), []);

  const headerNav = [
    { key: "/", to: "/", label: t("nav.top") },
    { key: "/company", to: "/company", label: t("nav.company"), title: t("nav.company_title") },
    { key: "/service", to: "/service", label: t("nav.service") },
    { key: "/development", to: "/development", label: t("nav.development") },
    { key: "/scientific_career", to: "/scientific_career", label: t("nav.scientific_career") },
    { key: "/faq", to: "/faq", label: t("nav.faq") },
    { key: "/recruit", to: "/recruit", label: t("nav.recruit") },
    { key: "/news", to: "/news", label: t("nav.news") },
    { key: "/column", to: "/column", label: t("nav.column") },
  ];

  useEffect(() => {
    closeDrawer();
  }, [location.pathname, closeDrawer]);

  useEffect(() => {
    if (!drawerOpen) {
      return;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [drawerOpen]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeDrawer();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeDrawer]);

  return (
    <>
      <div className="header">
        <LocalizedLink to="/" className="header__brand" onClick={closeDrawer}>
          <img
            className="logo"
            src={getImageUrl("/image/wesoft-home.png")}
            alt={t("footer.company_name")}
            width={250}
            height={78}
            decoding="async"
          />
        </LocalizedLink>

        <nav className="header__nav-desktop" aria-label="メインメニュー">

          <Menu
            theme="light"
            mode="horizontal"
            style={{
              flex: 1,
              minWidth: 0,
              maxWidth: "100%",
              display: "flex",
              justifyContent: "flex-end",
            }}
            selectedKeys={[selectedKey]}
          >
            {headerNav.map((item) => (
              <Menu.Item
                key={item.key}
                {...("title" in item && item.title ? { title: item.title } : {})}
              >
                <LocalizedLink to={item.to}>{item.label}</LocalizedLink>
              </Menu.Item>
            ))}
            <Menu.Item key="/inquiry">
              <LocalizedLink to="/inquiry">{t("nav.inquiry")}</LocalizedLink>
            </Menu.Item>
          </Menu>

          <div style={{ marginLeft: "12px", flexShrink: 0 }}>
            <LanguageSwitcher size="middle" />
          </div>
        </nav>

        <div className="header__mobile-bar">
          <LanguageSwitcher size="small" />

          <button
            type="button"
            className="header__menu-trigger"
            aria-expanded={drawerOpen}
            aria-controls="header-drawer"
            onClick={openDrawer}
          >
            <span className="header__menu-trigger-bar" aria-hidden />
            <span className="header__menu-trigger-bar" aria-hidden />
            <span className="header__menu-trigger-bar" aria-hidden />
            <span className="visually-hidden">メニューを開く</span>
          </button>
        </div>
      </div>

      <div
        id="header-drawer"
        className={
          drawerOpen ? "header-drawer header-drawer--open" : "header-drawer"
        }
        role="dialog"
        aria-modal="true"
        aria-labelledby={drawerTitleId}
        aria-hidden={!drawerOpen}
      >
        <div className="header-drawer__inner">
          <h2 id={drawerTitleId} className="visually-hidden">
            メインメニュー
          </h2>
          <div className="header-drawer__top">
            <LocalizedLink
              to="/"
              className="header-drawer__brand"
              onClick={closeDrawer}
            >
              <span className="visually-hidden">トップページへ</span>
              <img
                src={getImageUrl("/image/wesoft-home.png")}
                alt=""
                className="header-drawer__logo"
                width={220}
                height={48}
                decoding="async"
              />
            </LocalizedLink>
            <button
              type="button"
              className="header-drawer__close"
              onClick={closeDrawer}
              aria-label={t("buttons.close")}
            >
              ×
            </button>
          </div>

          <ul className="header-drawer__links">
            {headerNav.map((item) => (
              <li key={item.key} className="header-drawer__links-item">
                <LocalizedLink
                  to={item.to}
                  className={
                    selectedKey === item.key
                      ? "header-drawer__link header-drawer__link--current"
                      : "header-drawer__link"
                  }
                  onClick={closeDrawer}
                >
                  {item.label}
                </LocalizedLink>
              </li>
            ))}
          </ul>

          <div className="header-drawer__footer">

            <p className="header-drawer__tel">
              <a href={HEADER_TEL_HREF} className="header-drawer__tel-link">
                TEL.{HEADER_TEL_TEXT}
              </a>
            </p>
            <LocalizedLink
              to="/inquiry"
              className="header-drawer__cta"
              onClick={closeDrawer}
            >
              {t("footer.inquiry")}
            </LocalizedLink>
          </div>
        </div>
      </div>
    </>
  );
};

export default AppHeader;
